import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';

/**
 * Generischer Schrift-Einsteller: eine Element-Gruppe (H1, Fließtext, …) in einem Block.
 *
 * Kennt WEDER die LP-Seiten NOCH irgendeine andere Oberfläche — die Feldliste, Wertebereiche
 * und gesperrten Felder kommen als `catalog` herein. Der stammt vom Server
 * (`services/typography/typographyTokens.ts` → `catalogForScope`) und reist in der Antwort mit,
 * die der jeweilige Editor beim Öffnen ohnehin holt. Deshalb gibt es hier KEINE zweite,
 * driftende Fontliste im Frontend.
 *
 * Vertrag mit dem Server: dieselben Grenzen prüft `sanitizeTokens` noch einmal. Die Felder hier
 * sind Bequemlichkeit, nicht die Sicherung — wer per API einen Unsinnswert schickt, bekommt eine
 * 400 statt einer kaputten Regel im publizierten Dokument.
 */

export interface TypographyCatalog {
  scope: string;
  fonts: Array<{ key: string; label: string }>;
  weights: number[];
  alignments: string[];
  transforms: string[];
  ranges: {
    sizeDesktopPx: { min: number; max: number };
    sizeMobilePx: { min: number; max: number };
    lineHeight: { min: number; max: number };
  };
  mobileMaxWidthPx: number;
  elements: string[];
  lockedFields: string[];
  lockedReason: string | null;
}

export interface TextStyle {
  fontFamily?: string;
  sizeDesktopPx?: number;
  sizeMobilePx?: number;
  weight?: number;
  color?: string;
  align?: string;
  transform?: string;
  lineHeight?: number;
}

interface Props {
  label: string;
  /** Kurzer Satz, welche Elemente der Seite diese Gruppe trifft. */
  hint?: string;
  value: TextStyle;
  onChange: (next: TextStyle) => void;
  catalog: TypographyCatalog;
  /** Geerbte Werte des Scope-Defaults — erscheinen als Platzhalter statt als Wert. */
  inheritedFrom?: TextStyle;
  disabled?: boolean;
}

/** Leere Eingabe heißt „nicht gesetzt" (erbt), nicht „0". */
const numOrUndef = (raw: string): number | undefined => {
  const t = raw.trim();
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

const HEX = /^#[0-9a-fA-F]{6}$/;

const TypographyField: React.FC<Props> = ({
  label,
  hint,
  value,
  onChange,
  catalog,
  inheritedFrom,
  disabled = false,
}) => {
  const locked = (field: string) => catalog.lockedFields.includes(field);

  // Eine Eigenschaft setzen ODER entfernen. Das Löschen ist der Punkt: `{...value, x: undefined}`
  // würde den Schlüssel mit `undefined` behalten, JSON.stringify wirft ihn zwar weg, aber der
  // Vergleich „ist hier etwas gesetzt?" im Panel sähe ihn noch.
  const set = <K extends keyof TextStyle>(key: K, v: TextStyle[K]) => {
    const next: TextStyle = { ...value };
    if (v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))) delete next[key];
    else next[key] = v;
    onChange(next);
  };

  const ph = (key: keyof TextStyle, fallback: string): string => {
    const inh = inheritedFrom?.[key];
    return inh === undefined || inh === '' ? fallback : `${inh} (geerbt)`;
  };

  const colorInvalid = Boolean(value.color) && !HEX.test(String(value.color));

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box>
        <Typography variant="subtitle2">{label}</Typography>
        {hint && (
          <Typography variant="caption" color="text.secondary">
            {hint}
          </Typography>
        )}
      </Box>

      <Box display="flex" gap={1.5} flexWrap="wrap">
        <Tooltip title={locked('fontFamily') ? catalog.lockedReason || '' : ''}>
          <FormControl
            size="small"
            sx={{ minWidth: 190 }}
            disabled={disabled || locked('fontFamily')}
          >
            <InputLabel>Schriftart</InputLabel>
            <Select
              label="Schriftart"
              value={value.fontFamily ?? ''}
              onChange={(e) => set('fontFamily', (e.target.value as string) || undefined)}
            >
              <MenuItem value="">
                <em>{inheritedFrom?.fontFamily ? 'geerbt' : 'unverändert'}</em>
              </MenuItem>
              {catalog.fonts.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        <FormControl size="small" sx={{ minWidth: 130 }} disabled={disabled}>
          <InputLabel>Stärke</InputLabel>
          <Select
            label="Stärke"
            value={value.weight ?? ''}
            onChange={(e) =>
              set('weight', e.target.value === '' ? undefined : Number(e.target.value))
            }
          >
            <MenuItem value="">
              <em>{inheritedFrom?.weight ? `${inheritedFrom.weight} (geerbt)` : 'unverändert'}</em>
            </MenuItem>
            {catalog.weights.map((w) => (
              <MenuItem key={w} value={w}>
                {w}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="flex-start">
        <TextField
          size="small"
          type="number"
          label="Größe Desktop"
          disabled={disabled}
          value={value.sizeDesktopPx ?? ''}
          onChange={(e) => set('sizeDesktopPx', numOrUndef(e.target.value))}
          placeholder={ph('sizeDesktopPx', 'unverändert')}
          InputLabelProps={{ shrink: true }}
          inputProps={catalog.ranges.sizeDesktopPx}
          helperText={`px · ${catalog.ranges.sizeDesktopPx.min}–${catalog.ranges.sizeDesktopPx.max}`}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="number"
          label="Größe Mobil"
          disabled={disabled}
          value={value.sizeMobilePx ?? ''}
          onChange={(e) => set('sizeMobilePx', numOrUndef(e.target.value))}
          placeholder={ph('sizeMobilePx', 'wie Desktop')}
          InputLabelProps={{ shrink: true }}
          inputProps={catalog.ranges.sizeMobilePx}
          // Ohne eigenen Mobilwert gilt die Desktop-Größe auch auf dem Handy — bei den großen
          // Hero-Headlines ist das der Unterschied zwischen lesbar und zerschossen.
          helperText={`px · unter ${catalog.mobileMaxWidthPx}px`}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="number"
          label="Zeilenhöhe"
          disabled={disabled}
          value={value.lineHeight ?? ''}
          onChange={(e) => set('lineHeight', numOrUndef(e.target.value))}
          placeholder={ph('lineHeight', 'unverändert')}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 0.05, ...catalog.ranges.lineHeight }}
          helperText="ohne Einheit"
          sx={{ width: 130 }}
        />
      </Box>

      <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="Farbe"
          disabled={disabled}
          value={value.color ?? ''}
          onChange={(e) => set('color', e.target.value.trim() || undefined)}
          placeholder={ph('color', '#rrggbb')}
          InputLabelProps={{ shrink: true }}
          error={colorInvalid}
          helperText={colorInvalid ? 'Format #rrggbb' : ' '}
          sx={{ width: 150 }}
        />
        {/* Nativer Farbwähler: schreibt IMMER #rrggbb, deckt also die Formatfalle ab. */}
        <input
          type="color"
          aria-label={`${label} Farbe wählen`}
          disabled={disabled}
          value={HEX.test(String(value.color)) ? String(value.color) : '#ffffff'}
          onChange={(e) => set('color', e.target.value)}
          style={{
            width: 42,
            height: 38,
            marginTop: 2,
            padding: 0,
            border: '1px solid #c4c4c4',
            borderRadius: 4,
            background: 'none',
            cursor: disabled ? 'default' : 'pointer',
          }}
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          disabled={disabled}
          value={value.align ?? null}
          onChange={(_e, v) => set('align', v ?? undefined)}
        >
          {catalog.alignments.map((a) => (
            <ToggleButton key={a} value={a}>
              {{ left: 'links', center: 'mitte', right: 'rechts' }[a] ?? a}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <ToggleButtonGroup
          size="small"
          exclusive
          disabled={disabled}
          value={value.transform ?? null}
          onChange={(_e, v) => set('transform', v ?? undefined)}
        >
          <ToggleButton value="uppercase">GROSS</ToggleButton>
          <ToggleButton value="none">normal</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
};

export default TypographyField;
